import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MidiNote, MidiTimeSignature } from "@midi-fantom-utils/midi-core";
import { measureBoundaries, type TrackLane } from "@midi-fantom-utils/midi-util";
import { PIANO_ROLL_COLORS } from "../pianoRollColors.js";
import { DRUM_COLOR, channelColor } from "../channelColors.js";
import type { PianoRollMarker } from "./PianoRoll.js";
import {
  clamp,
  lowerBound,
  resizeCanvas,
  useRafFrame,
  strokeHGroup,
  strokeVGroup,
  renderTimeGrid,
  renderVGridCache,
  blitVGridCache,
  vGridCacheNeedsRender,
  ensureVGridCache,
  createVGridCache,
  type VGridCache,
  type TimeGridLevels,
  IS_DEV,
  profileDrawFrame,
} from "../canvas-utils.js";
import s from "./TrackLanes.module.css";

/**
 * Лейновый таймлайн «как Bitwig»: горизонтальная ось = время (общая),
 * вертикаль = дорожки (лейны). Каждая дорожка — полоса фиксированной высоты,
 * внутри которой ноты рисуются по питчу (масштаб подстраивается под диапазон
 * дорожки). Слева — заголовки дорожек (рисует `TrackList` в отдельной
 * колонке), сверху — линейка тактов и полоска маркеров, вертикальная линия —
 * playhead.
 *
 * Вертикальный скролл дорожек управляется здесь (колесо/скроллбар) и
 * публикуется через `onScrollYChange` для синхронизации левого `TrackList`.
 */

export type TrackLanesProps = {
  /** Дорожки (результат `buildTrackLanes`). */
  lanes: ReadonlyArray<TrackLane>;
  width: number;
  height: number;
  /** Высота одного лейна, px. */
  laneHeight?: number;
  ppq: number;
  timeSignatures: ReadonlyArray<MidiTimeSignature>;
  playheadTick?: number;
  onSeek?: (tick: number) => void;
  /** Видимые дорожки (Set id). undefined = все. */
  enabledLanes?: ReadonlySet<string>;
  /** Заглушенные дорожки (Set id) — рисуются затемнёнными. */
  mutedLanes?: ReadonlySet<string>;
  markerStripHeight?: number;
  markers?: ReadonlyArray<PianoRollMarker>;
  onAddMarker?: (tick: number) => void;
  onMoveMarker?: (id: string, tick: number) => void;
  onRenameMarker?: (id: string, name: string) => void;
  onDeleteMarker?: (id: string) => void;
  /** Текущий вертикальный скролл (для синхронизации TrackList). */
  scrollY?: number;
  onScrollYChange?: (y: number) => void;
};

const MIN_PX_PER_BEAT = 2;
const MAX_PX_PER_BEAT = 256;
const MIN_PX_PER_BEAT_FIT = 4;
const RULER_H = 20;
const DEFAULT_MARKER_STRIP_H = 22;
const ZOOM_STEP = 1.25;
const ZOOM_WHEEL_FACTOR = 0.0015;
const PAD_MEASURES = 1;
/** Горизонтальный запас offscreen-кэша вертикальной сетки, px. */
const GRID_CACHE_MARGIN = 160;
const SCROLLBAR_MIN = 20;
const PAN_THRESHOLD = 3;
const NOTE_PAD = 2;

type ViewState = {
  pxPerBeat: number;
  scrollXPx: number;
  scrollYPx: number;
};

const clampViewState = (
  v: ViewState,
  viewport: { w: number; h: number },
  totalPx: number,
  totalYPx: number
): ViewState => ({
  pxPerBeat: clamp(v.pxPerBeat, MIN_PX_PER_BEAT, MAX_PX_PER_BEAT),
  scrollXPx: clamp(v.scrollXPx, 0, Math.max(0, totalPx - viewport.w)),
  scrollYPx: clamp(v.scrollYPx, 0, Math.max(0, totalYPx - viewport.h)),
});

/** Видимая дорожка: лейн + геометрия (диапазон питча, отсортированные ноты). */
type DisplayLane = {
  lane: TrackLane;
  minMidi: number;
  maxMidi: number;
  span: number;
  sortedNotes: ReadonlyArray<MidiNote>;
};

const buildDisplayLanes = (
  lanes: ReadonlyArray<TrackLane>,
  enabled: ReadonlySet<string> | undefined
): DisplayLane[] => {
  const out: DisplayLane[] = [];
  for (const lane of lanes) {
    if (enabled && !enabled.has(lane.id)) continue;
    const notes = lane.notes.slice().sort((a, b) => a.ticks - b.ticks);
    let lo = 127;
    let hi = 0;
    for (const n of notes) {
      if (n.midi < lo) lo = n.midi;
      if (n.midi > hi) hi = n.midi;
    }
    if (notes.length === 0) {
      lo = 48;
      hi = 72;
    }
    let span = hi - lo + 1;
    if (span < 12) {
      const expand = Math.ceil((12 - span) / 2);
      lo = Math.max(0, lo - expand);
      hi = Math.min(127, hi + expand);
      span = hi - lo + 1;
    }
    out.push({ lane, minMidi: lo, maxMidi: hi, span, sortedNotes: notes });
  }
  return out;
};

export const TrackLanes = ({
  lanes,
  width,
  height,
  laneHeight = 64,
  ppq,
  timeSignatures,
  playheadTick,
  onSeek,
  enabledLanes,
  mutedLanes,
  markerStripHeight = DEFAULT_MARKER_STRIP_H,
  markers = [],
  onAddMarker,
  onMoveMarker,
  onRenameMarker,
  onDeleteMarker,
  scrollY,
  onScrollYChange,
}: TrackLanesProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rulerRef = useRef<HTMLCanvasElement | null>(null);
  const markerRef = useRef<HTMLCanvasElement | null>(null);
  const markerWrapRef = useRef<HTMLDivElement | null>(null);
  const hoverRef = useRef<HTMLDivElement | null>(null);

  const [editingMarkerId, setEditingMarkerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const markerDragRef = useRef<{ id: string } | null>(null);
  const markerMeasureRef = useRef<number | null>(null);
  const panRef = useRef<{
    startClientX: number;
    startClientY: number;
    startScrollX: number;
    startScrollY: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  const thumbDragRef = useRef<
    null | { axis: "x" | "y"; startClient: number; startScroll: number }
  >(null);
  const suppressMouseRef = useRef(false);

  const mainBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const rulerBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const markerBackingRef = useRef<{ w: number; h: number; dpr: number } | null>(null);
  const vGridRef = useRef<VGridCache>(createVGridCache());

  const displayLanes = useMemo(
    () => buildDisplayLanes(lanes, enabledLanes),
    [lanes, enabledLanes]
  );

  const songEndTick = useMemo(() => {
    let max = 0;
    for (const lane of lanes) {
      for (const n of lane.notes) {
        max = Math.max(max, n.ticks + n.durationTicks);
      }
    }
    return Math.max(max, ppq * 4);
  }, [lanes, ppq]);

  const boundaries = useMemo(
    () => measureBoundaries(ppq, timeSignatures, songEndTick),
    [ppq, timeSignatures, songEndTick]
  );

  const [viewport, setViewport] = useState<{ w: number; h: number }>({
    w: width,
    h: height,
  });
  const viewportRef = useRef<{ w: number; h: number }>({ w: width, h: height });

  const totalYPx = displayLanes.length * laneHeight;

  const initialView = useMemo<ViewState>(() => {
    const lastBoundary = boundaries[boundaries.length - 1] ?? 0;
    const pxPerBeat = clamp(
      (width * ppq) / Math.max(1, lastBoundary),
      MIN_PX_PER_BEAT_FIT,
      MAX_PX_PER_BEAT
    );
    return { pxPerBeat, scrollXPx: 0, scrollYPx: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [viewState, setViewState] = useState<ViewState>(initialView);
  const viewStateRef = useRef<ViewState>(initialView);

  const boundariesRef = useRef(boundaries);
  const displayLanesRef = useRef(displayLanes);
  const ppqRef = useRef(ppq);
  const playheadRef = useRef(playheadTick);
  const laneHeightRef = useRef(laneHeight);

  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);
  useEffect(() => {
    boundariesRef.current = boundaries;
  }, [boundaries]);
  useEffect(() => {
    displayLanesRef.current = displayLanes;
  }, [displayLanes]);
  useEffect(() => {
    ppqRef.current = ppq;
  }, [ppq]);
  useEffect(() => {
    playheadRef.current = playheadTick;
  }, [playheadTick]);
  useEffect(() => {
    laneHeightRef.current = laneHeight;
  }, [laneHeight]);

  const totalPxOf = (vs: ViewState): number =>
    (boundariesRef.current[boundariesRef.current.length - 1] ?? 0) *
    (vs.pxPerBeat / ppqRef.current);

  const applyViewState = useCallback((updater: (v: ViewState) => ViewState) => {
    const next = clampViewState(
      updater(viewStateRef.current),
      viewportRef.current,
      totalPxOf(viewStateRef.current),
      displayLanesRef.current.length * laneHeightRef.current
    );
    viewStateRef.current = next;
    setViewState(next);
    onScrollYChange?.(next.scrollYPx);
  }, [onScrollYChange]);

  // При внешнем изменении scrollY (например, сброс на новом файле)
  // синхронизируем внутренний стейт.
  useEffect(() => {
    if (typeof scrollY !== "number") return;
    const vs = viewStateRef.current;
    const next = clampViewState(
      { ...vs, scrollYPx: scrollY },
      viewportRef.current,
      totalPxOf(vs),
      displayLanesRef.current.length * laneHeightRef.current
    );
    if (Math.abs(next.scrollYPx - vs.scrollYPx) > 0.5) {
      viewStateRef.current = next;
      setViewState(next);
    }
  }, [scrollY]);

  const fitView = useCallback(() => {
    const vp = viewportRef.current;
    const b = boundariesRef.current;
    const lastBoundary = b[b.length - 1] ?? 0;
    const pxPerBeat = clamp(
      (vp.w * ppqRef.current) / Math.max(1, lastBoundary),
      MIN_PX_PER_BEAT_FIT,
      MAX_PX_PER_BEAT
    );
    applyViewState(() => ({ pxPerBeat, scrollXPx: 0, scrollYPx: 0 }));
  }, [applyViewState]);

  const fitRef = useRef(fitView);
  const applyViewStateRef = useRef(applyViewState);
  useEffect(() => {
    fitRef.current = fitView;
    applyViewStateRef.current = applyViewState;
  }, [fitView, applyViewState]);

  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      let w = 0;
      let h = 0;
      for (const e of entries) {
        if (e.target === wrap) {
          w = e.contentRect.width;
          h = e.contentRect.height;
        }
      }
      if (w <= 0 || h <= 0) return;
      const prev = viewportRef.current;
      viewportRef.current = { w, h };
      if (prev.w !== w || prev.h !== h) {
        setViewport({ w, h });
      }
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // Рефит при смене структуры песни (новый файл / изменение дорожек).
  const contentKey = useMemo(
    () =>
      `${lanes.length}|${songEndTick}|${timeSignatures
        .map((t) => `${t.ticks}:${t.timeSignature[0]}/${t.timeSignature[1]}`)
        .join(",")}`,
    [lanes.length, songEndTick, timeSignatures]
  );
  const lastFitKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastFitKeyRef.current !== contentKey) {
      lastFitKeyRef.current = contentKey;
      fitRef.current();
    }
  }, [contentKey]);

  const drawMain = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = resizeCanvas(canvas, viewport.w, viewport.h, mainBackingRef);
    if (!ctx) return;
    const colorsVal = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerTick = vs.pxPerBeat / ppq;
    const lanesArr = displayLanes;

    ctx.fillStyle = "var(--surface-2)";
    ctx.fillRect(0, 0, viewport.w, viewport.h);
    ctx.translate(-vs.scrollXPx, -vs.scrollYPx);

    const x0 = vs.scrollXPx;
    const x1 = vs.scrollXPx + viewport.w;
    const y0 = vs.scrollYPx;
    const y1 = vs.scrollYPx + viewport.h;
    const padX = ppq * pxPerTick * PAD_MEASURES;

    // Ноты по лейнам.
    for (let i = 0; i < lanesArr.length; i += 1) {
      const dl = lanesArr[i]!;
      const laneTop = i * laneHeight;
      if (laneTop + laneHeight < y0) continue;
      if (laneTop > y1) break;
      const innerH = laneHeight - NOTE_PAD * 2;
      const pxPerKey = innerH / Math.max(1, dl.span);
      const muted = mutedLanes?.has(dl.lane.id) ?? false;
      const color = dl.lane.isDrum ? DRUM_COLOR : channelColor(dl.lane.channel);

      const notes = dl.sortedNotes;
      const cullStart = (x0 - padX) / pxPerTick;
      const cullEnd = (x1 + padX) / pxPerTick;
      const loIdx = lowerBound(notes, cullStart, (n) => n.ticks);
      for (let j = loIdx; j < notes.length; j += 1) {
        const n = notes[j]!;
        if (n.ticks > cullEnd) break;
        const nx = n.ticks * pxPerTick;
        const ny = laneTop + NOTE_PAD + (dl.maxMidi - n.midi) * pxPerKey;
        const nw = Math.max(2, n.durationTicks * pxPerTick);
        const nh = Math.max(2, pxPerKey - 0.5);
        if (muted) {
          ctx.globalAlpha = 0.25;
          ctx.fillStyle = color;
        } else {
          ctx.globalAlpha = 0.6 + 0.4 * n.velocity;
          ctx.fillStyle = color;
        }
        ctx.fillRect(nx, ny, nw, nh);
        ctx.globalAlpha = 1;
      }
    }

    // Горизонтальные линии-разделители лейнов (батч: один stroke на стиль).
    const sepYs: number[] = [];
    for (let i = 0; i <= lanesArr.length; i += 1) {
      const y = i * laneHeight;
      if (y < y0 || y > y1) continue;
      sepYs.push(y);
    }
    strokeHGroup(ctx, sepYs, x0, x1, colorsVal.gridFaint, 1);

    // Вертикальная сетка тактов/долей (поверх нот). Фаза 2: рисуется в
    // offscreen-канвас при изменении зума/контента/скролла за край кэша,
    // при скролле в пределах кэша — drawImage со сдвигом. Фоллбэк (нет
    // OffscreenCanvas) — прямое батченное рисование каждый кадр.
    const gridColors = {
      strong: colorsVal.gridStrong,
      med: colorsVal.gridMed,
      faint: colorsVal.gridFaint,
      sub: colorsVal.gridSub,
    };
    const gridLevels: TimeGridLevels = { med: true };
    const gridDpr = mainBackingRef.current?.dpr ?? 1;
    const gridCacheCtx = ensureVGridCache(
      vGridRef.current,
      viewport.w,
      viewport.h,
      gridDpr,
      GRID_CACHE_MARGIN
    );
    if (gridCacheCtx) {
      const gridKey = `${pxPerTick}|${ppq}|${contentKey}`;
      if (vGridCacheNeedsRender(vGridRef.current, gridKey, vs.scrollXPx)) {
        renderVGridCache(
          vGridRef.current,
          viewport.w,
          viewport.h,
          gridDpr,
          GRID_CACHE_MARGIN,
          vs.scrollXPx,
          boundaries,
          timeSignatures,
          ppq,
          pxPerTick,
          gridLevels,
          gridColors
        );
        vGridRef.current.key = gridKey;
      }
      blitVGridCache(ctx, vGridRef.current, vs.scrollYPx);
    } else {
      renderTimeGrid(
        ctx,
        boundaries,
        timeSignatures,
        ppq,
        pxPerTick,
        x0 - padX,
        x1 + padX,
        y0,
        y1,
        gridLevels,
        gridColors
      );
    }

    // Playhead.
    if (typeof playheadTick === "number" && playheadTick >= 0) {
      const px = playheadTick * pxPerTick;
      ctx.strokeStyle = "rgba(20, 30, 60, 0.85)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(px, y0);
      ctx.lineTo(px, y1);
      ctx.stroke();
    }
  }, [
    boundaries,
    contentKey,
    displayLanes,
    laneHeight,
    mutedLanes,
    playheadTick,
    ppq,
    timeSignatures,
    viewState,
    viewport,
  ]);

  const drawRuler = useCallback(() => {
    const canvas = rulerRef.current;
    if (!canvas) return;
    const ctx = resizeCanvas(canvas, viewport.w, RULER_H, rulerBackingRef);
    if (!ctx) return;
    const colorsVal = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerTick = vs.pxPerBeat / ppq;
    ctx.fillStyle = "var(--surface-2)";
    ctx.fillRect(0, 0, viewport.w, RULER_H);
    ctx.translate(-vs.scrollXPx, 0);
    const x0 = vs.scrollXPx - 60;
    const x1 = vs.scrollXPx + viewport.w + 60;
    // Старт цикла через бинарный поиск, линии — батчем в один stroke.
    const tickMin = x0 / pxPerTick;
    const xs: number[] = [];
    const startIdx = lowerBound(boundaries, tickMin, (v) => v);
    for (let i = startIdx; i < boundaries.length; i += 1) {
      const mx = boundaries[i]! * pxPerTick;
      if (mx > x1) break;
      xs.push(mx);
    }
    strokeVGroup(ctx, xs, RULER_H - 6, RULER_H, colorsVal.gridStrong, 1);
    ctx.fillStyle = colorsVal.keyLabel;
    ctx.font = "9px ui-monospace, monospace";
    for (let k = 0; k < xs.length; k += 1) {
      ctx.fillText("M" + (startIdx + k + 1), xs[k]! + 3, 11);
    }
  }, [boundaries, ppq, viewState, viewport]);

  const drawMarkerStrip = useCallback(() => {
    const canvas = markerRef.current;
    if (!canvas) return;
    const ctx = resizeCanvas(canvas, viewport.w, markerStripHeight, markerBackingRef);
    if (!ctx) return;
    const colorsVal = PIANO_ROLL_COLORS;
    const vs = viewState;
    const pxPerTick = vs.pxPerBeat / ppq;
    ctx.fillStyle = "var(--surface-2)";
    ctx.fillRect(0, 0, viewport.w, markerStripHeight);
    ctx.translate(-vs.scrollXPx, 0);
    const x0 = vs.scrollXPx - 60;
    const x1 = vs.scrollXPx + viewport.w + 60;
    const xs: number[] = [];
    const startIdx = lowerBound(boundaries, x0 / pxPerTick, (v) => v);
    for (let i = startIdx; i < boundaries.length; i += 1) {
      const mx = boundaries[i]! * pxPerTick;
      if (mx > x1) break;
      xs.push(mx);
    }
    strokeVGroup(ctx, xs, 0, markerStripHeight, colorsVal.gridFaint, 1);
  }, [boundaries, markerStripHeight, ppq, viewState, viewport]);

  const drawFrame = useCallback(() => {
    const t0 = IS_DEV && typeof performance !== "undefined" ? performance.now() : 0;
    drawMain();
    drawRuler();
    drawMarkerStrip();
    profileDrawFrame("TrackLanes", t0);
  }, [drawMain, drawMarkerStrip, drawRuler]);

  useRafFrame(drawFrame);

  const pointerGate = useCallback((type: string): boolean => {
    if (type.startsWith("pointer")) {
      suppressMouseRef.current = true;
      return false;
    }
    if (suppressMouseRef.current) {
      suppressMouseRef.current = false;
      return true;
    }
    return false;
  }, []);

  const tickAtClientX = useCallback(
    (clientX: number): number => {
      const canvas = canvasRef.current;
      const vs = viewStateRef.current;
      if (!canvas) return 0;
      const rect = canvas.getBoundingClientRect();
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const x = clientX - rect.left;
      return Math.max(0, Math.round((vs.scrollXPx + x) / pxPerTick));
    },
    []
  );

  const tickAtMarkerClientX = useCallback(
    (clientX: number): number => {
      const wrap = markerWrapRef.current;
      const vs = viewStateRef.current;
      if (!wrap) return 0;
      const rect = wrap.getBoundingClientRect();
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const x = clientX - rect.left;
      return Math.max(0, Math.round((vs.scrollXPx + x) / pxPerTick));
    },
    []
  );

  const measureIndexAtTick = useCallback((tick: number): number => {
    const b = boundariesRef.current;
    if (b.length === 0) return 0;
    for (let i = b.length - 1; i >= 0; i -= 1) {
      if (b[i]! <= tick) return i;
    }
    return 0;
  }, []);

  const zoomAt = useCallback(
    (cx: number, factor: number) => {
      const vs = viewStateRef.current;
      const vp = viewportRef.current;
      const pxPerTick = vs.pxPerBeat / ppqRef.current;
      const tickAnchor = (vs.scrollXPx + cx) / pxPerTick;
      const nextPxPerBeat = clamp(
        vs.pxPerBeat * factor,
        MIN_PX_PER_BEAT,
        MAX_PX_PER_BEAT
      );
      const nextScrollX = tickAnchor * (nextPxPerBeat / ppqRef.current) - cx;
      applyViewState((v) => ({
        ...v,
        pxPerBeat: nextPxPerBeat,
        scrollXPx: nextScrollX,
      }));
    },
    [applyViewState]
  );

  const zoomAtRef = useRef(zoomAt);
  useEffect(() => {
    zoomAtRef.current = zoomAt;
  }, [zoomAt]);

  const scrollBy = useCallback(
    (delta: number, vertical: boolean) => {
      applyViewState((v) =>
        vertical
          ? { ...v, scrollYPx: v.scrollYPx + delta }
          : { ...v, scrollXPx: v.scrollXPx + delta }
      );
    },
    [applyViewState]
  );

  const scrollByRef = useRef(scrollBy);
  useEffect(() => {
    scrollByRef.current = scrollBy;
  }, [scrollBy]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        zoomAtRef.current(
          e.clientX - rect.left,
          Math.exp(-e.deltaY * ZOOM_WHEEL_FACTOR)
        );
        return;
      }
      const vs = viewStateRef.current;
      const vp = viewportRef.current;
      const totalPx = totalPxOf(vs);
      const totalYPx = displayLanesRef.current.length * laneHeightRef.current;
      const hOverflow = totalPx > vp.w;
      const vOverflow = totalYPx > vp.h;
      if (e.deltaX !== 0 && hOverflow) {
        e.preventDefault();
        scrollByRef.current(e.deltaX, false);
        return;
      }
      if (vOverflow) {
        e.preventDefault();
        scrollByRef.current(e.deltaY, true);
      } else if (hOverflow && e.deltaY !== 0) {
        e.preventDefault();
        scrollByRef.current(e.deltaY, false);
      }
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoomIn = useCallback(() => {
    zoomAtRef.current(viewportRef.current.w / 2, ZOOM_STEP);
  }, []);
  const handleZoomOut = useCallback(() => {
    zoomAtRef.current(viewportRef.current.w / 2, 1 / ZOOM_STEP);
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointerGate(e.type)) return;
    const vs = viewStateRef.current;
    e.preventDefault();
    if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    containerRef.current?.focus({ preventScroll: true });
    panRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startScrollX: vs.scrollXPx,
      startScrollY: vs.scrollYPx,
    };
    panMovedRef.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointerGate(e.type)) return;
    const pan = panRef.current;
    if (pan) {
      const dx = e.clientX - pan.startClientX;
      const dy = e.clientY - pan.startClientY;
      if (!panMovedRef.current && (Math.abs(dx) > PAN_THRESHOLD || Math.abs(dy) > PAN_THRESHOLD)) {
        panMovedRef.current = true;
      }
      if (panMovedRef.current) {
        applyViewState((v) => ({
          ...v,
          scrollXPx: pan.startScrollX - dx,
          scrollYPx: pan.startScrollY - dy,
        }));
      }
      return;
    }
    const tick = tickAtClientX(e.clientX);
    if (hoverRef.current) {
      hoverRef.current.textContent = "t=" + String(tick);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (pointerGate(e.type)) return;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.releasePointerCapture === "function") {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (panRef.current) {
      if (!panMovedRef.current) {
        const tick = tickAtClientX(e.clientX);
        if (onSeek) onSeek(tick);
      }
      panRef.current = null;
      panMovedRef.current = false;
    }
  };

  const handlePointerLeave = () => {
    suppressMouseRef.current = false;
    if (hoverRef.current) hoverRef.current.textContent = "";
  };

  // --- Маркеры (полоска) ---

  const startMarkerDrag = (e: React.PointerEvent<HTMLElement>, id: string) => {
    if (pointerGate(e.type)) return;
    e.preventDefault();
    e.stopPropagation();
    setEditingMarkerId(null);
    markerDragRef.current = { id };
    setActiveMarkerId(id);
    if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const moveMarkerDrag = (e: React.PointerEvent<HTMLElement>) => {
    if (pointerGate(e.type)) return;
    const drag = markerDragRef.current;
    if (!drag) return;
    const tick = tickAtMarkerClientX(e.clientX);
    const measure = measureIndexAtTick(tick);
    if (markerMeasureRef.current !== measure) {
      markerMeasureRef.current = measure;
      const b = boundariesRef.current;
      onMoveMarker?.(drag.id, b[measure]!);
    }
  };

  const endMarkerDrag = (e: React.PointerEvent<HTMLElement>) => {
    if (pointerGate(e.type)) return;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.releasePointerCapture === "function") {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    markerDragRef.current = null;
    markerMeasureRef.current = null;
    setActiveMarkerId(null);
  };

  const handleMarkerWrapPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerGate(e.type)) return;
    const tick = tickAtMarkerClientX(e.clientX);
    const measure = measureIndexAtTick(tick);
    const b = boundariesRef.current;
    onAddMarker?.(b[measure]!);
  };

  const commitRename = () => {
    if (editingMarkerId) {
      onRenameMarker?.(editingMarkerId, editingName);
      setEditingMarkerId(null);
      setEditingName("");
    }
  };

  const markerChips = useMemo(() => {
    const pxPerTick = viewState.pxPerBeat / ppq;
    const out: Array<{ id: string; measure: number; name: string; left: number }> = [];
    for (const m of markers) {
      const tick = boundaries[m.measure - 1];
      if (tick === undefined) continue;
      const mx = tick * pxPerTick - viewState.scrollXPx;
      if (mx < -60 || mx > viewport.w + 60) continue;
      out.push({ id: m.id, measure: m.measure, name: m.name, left: mx });
    }
    return out;
  }, [boundaries, markers, ppq, viewState.pxPerBeat, viewState.scrollXPx, viewport.w]);

  const editingX = useMemo(() => {
    if (!editingMarkerId) return null;
    const m = markers.find((x) => x.id === editingMarkerId);
    if (!m) return null;
    const tick = boundaries[m.measure - 1];
    if (tick === undefined) return null;
    return tick * (viewState.pxPerBeat / ppq) - viewState.scrollXPx;
  }, [boundaries, editingMarkerId, markers, ppq, viewState]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const k = e.key;
    if (k === "+" || k === "=") {
      e.preventDefault();
      handleZoomIn();
    } else if (k === "-") {
      e.preventDefault();
      handleZoomOut();
    } else if (k === "0") {
      e.preventDefault();
      fitRef.current();
    } else if (k === "ArrowLeft") {
      e.preventDefault();
      scrollByRef.current(-40, false);
    } else if (k === "ArrowRight") {
      e.preventDefault();
      scrollByRef.current(40, false);
    } else if (k === "ArrowUp") {
      e.preventDefault();
      scrollByRef.current(-40, true);
    } else if (k === "ArrowDown") {
      e.preventDefault();
      scrollByRef.current(40, true);
    }
  };

  const startThumbDrag = (e: React.PointerEvent<HTMLDivElement>, axis: "x" | "y") => {
    if (pointerGate(e.type)) return;
    e.preventDefault();
    const vs = viewStateRef.current;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    thumbDragRef.current = {
      axis,
      startClient: axis === "x" ? e.clientX : e.clientY,
      startScroll: axis === "x" ? vs.scrollXPx : vs.scrollYPx,
    };
  };

  const moveThumb = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerGate(e.type)) return;
    const td = thumbDragRef.current;
    if (!td) return;
    const vs = viewStateRef.current;
    const vp = viewportRef.current;
    const cur = td.axis === "x" ? e.clientX : e.clientY;
    const delta = cur - td.startClient;
    if (td.axis === "x") {
      const totalPx = totalPxOf(vs);
      const ratio = totalPx / Math.max(1, vp.w);
      applyViewState((v) => ({ ...v, scrollXPx: td.startScroll + delta * ratio }));
    } else {
      const totalYPx = displayLanesRef.current.length * laneHeightRef.current;
      const ratio = totalYPx / Math.max(1, vp.h);
      applyViewState((v) => ({ ...v, scrollYPx: td.startScroll + delta * ratio }));
    }
  };

  const endThumb = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerGate(e.type)) return;
    if (typeof e.pointerId === "number" && typeof e.currentTarget.releasePointerCapture === "function") {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    thumbDragRef.current = null;
  };

  const pxPerTick = viewState.pxPerBeat / ppq;
  const totalPx = (boundaries[boundaries.length - 1] ?? 0) * pxPerTick;
  const totalYPxVisible = displayLanes.length * laneHeight;
  const hVisible = totalPx > viewport.w;
  const vVisible = totalYPxVisible > viewport.h;
  const thumbW = hVisible ? Math.max(SCROLLBAR_MIN, (viewport.w / totalPx) * viewport.w) : 0;
  const thumbX = hVisible ? (viewState.scrollXPx / totalPx) * viewport.w : 0;
  const thumbH = vVisible ? Math.max(SCROLLBAR_MIN, (viewport.h / totalYPxVisible) * viewport.h) : 0;
  const thumbY = vVisible ? (viewState.scrollYPx / totalYPxVisible) * viewport.h : 0;

  return (
    <div
      ref={containerRef}
      className={s.container}
      tabIndex={0}
      style={{ height }}
      onKeyDown={handleKeyDown}
    >
      <div className={s.toolbar}>
        <button type="button" className={s.toolbarBtn} onClick={handleZoomOut} aria-label="Zoom out">
          −
        </button>
        <button type="button" className={s.toolbarBtn} onClick={handleZoomIn} aria-label="Zoom in">
          +
        </button>
        <button type="button" className={s.toolbarBtn} onClick={() => fitRef.current()} aria-label="Fit">
          Fit
        </button>
        <span className={s.readout} data-testid="track-lanes-readout">
          1 доля = {Math.round(viewState.pxPerBeat)}px
        </span>
      </div>
      <div className={s.body}>
        <div className={s.mainCol}>
          <div
            ref={markerWrapRef}
            className={s.markerStripWrap}
            style={{ height: markerStripHeight }}
            data-role="track-lanes-marker-strip"
            onPointerDown={handleMarkerWrapPointerDown}
            onMouseDown={handleMarkerWrapPointerDown as unknown as React.MouseEventHandler<HTMLDivElement>}
          >
            <canvas ref={markerRef} className={s.markerStrip} data-role="track-lanes-markers" />
            {markerChips.map((m) => (
              <span
                key={m.id}
                className={`${s.markerChip} ${activeMarkerId === m.id ? s.markerChipActive : ""}`}
                style={{ left: m.left }}
                data-marker-id={m.id}
                data-testid="track-lanes-marker-chip"
                onPointerDown={(e) => startMarkerDrag(e, m.id)}
                onPointerMove={moveMarkerDrag}
                onPointerUp={endMarkerDrag}
                onPointerCancel={endMarkerDrag}
                onMouseDown={startMarkerDrag as unknown as React.MouseEventHandler<HTMLSpanElement>}
                onMouseMove={moveMarkerDrag as unknown as React.MouseEventHandler<HTMLSpanElement>}
                onMouseUp={endMarkerDrag as unknown as React.MouseEventHandler<HTMLSpanElement>}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  setEditingMarkerId(m.id);
                  setEditingName(m.name);
                }}
              >
                <span className={s.markerChipLabel}>
                  #{m.measure}
                  {m.name ? ` ${m.name}` : ""}
                </span>
                <button
                  type="button"
                  className={s.markerDelete}
                  data-testid="track-lanes-marker-delete"
                  aria-label={`Удалить метку ${m.measure}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteMarker?.(m.id);
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {editingMarkerId !== null && editingX !== null ? (
              <input
                className={s.markerInput}
                style={{ left: editingX }}
                value={editingName}
                autoFocus
                data-testid="track-lanes-marker-input"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === "Escape") {
                    setEditingMarkerId(null);
                    setEditingName("");
                  }
                }}
              />
            ) : null}
          </div>
          <canvas ref={rulerRef} className={s.ruler} data-role="track-lanes-ruler" />
          <div ref={canvasWrapRef} className={s.canvasWrap}>
            <canvas
              ref={canvasRef}
              className={s.canvas}
              data-role="track-lanes-main"
              style={{ cursor: "pointer", touchAction: "none" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              onMouseDown={handlePointerDown as unknown as React.MouseEventHandler<HTMLCanvasElement>}
              onMouseMove={handlePointerMove as unknown as React.MouseEventHandler<HTMLCanvasElement>}
              onMouseUp={handlePointerUp as unknown as React.MouseEventHandler<HTMLCanvasElement>}
              onMouseLeave={handlePointerLeave as unknown as React.MouseEventHandler<HTMLCanvasElement>}
            />
            <div ref={hoverRef} className={s.hoverHint} data-role="track-lanes-hover" />
            <div
              className={s.hScrollbar}
              data-testid="track-lanes-hscroll"
              style={{ display: hVisible ? undefined : "none" }}
            >
              <div
                className={s.hThumb}
                style={{ left: thumbX, width: thumbW }}
                onPointerDown={(e) => startThumbDrag(e, "x")}
                onPointerMove={moveThumb}
                onPointerUp={endThumb}
                onPointerCancel={endThumb}
                onMouseDown={startThumbDrag as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseMove={moveThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseUp={endThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
              />
            </div>
            <div
              className={s.vScrollbar}
              data-testid="track-lanes-vscroll"
              style={{ display: vVisible ? undefined : "none" }}
            >
              <div
                className={s.vThumb}
                style={{ top: thumbY, height: thumbH }}
                onPointerDown={(e) => startThumbDrag(e, "y")}
                onPointerMove={moveThumb}
                onPointerUp={endThumb}
                onPointerCancel={endThumb}
                onMouseDown={startThumbDrag as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseMove={moveThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
                onMouseUp={endThumb as unknown as React.MouseEventHandler<HTMLDivElement>}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
